import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@core/index";
import {
  Mic,
  MicOff,
  Bot,
  Wifi,
  WifiOff,
  PhoneOff,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react";
import {
  Room,
  RoomEvent,
  type RemoteParticipant,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
} from "livekit-client";

// Transcript entry type definition
type TranscriptEntry = {
  identity: string;
  text: string;
  timestamp: Date;
  isAgent: boolean;
};

const ConversationPracticeView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [room, setRoom] = useState<Room | null>(null);
  const [transcripts, setTranscripts] = useState<TranscriptEntry[]>([]);
  const [isAgentConnected, setIsAgentConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showEndCallPopup, setShowEndCallPopup] = useState(false);
  const [livekitUrl, setLivekitUrl] = useState<string | undefined>(undefined);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [isConnecting, setIsConnecting] = useState(false);

  const audioContainerRef = useRef<HTMLDivElement>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new transcripts arrive
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcripts]);

  // Helper to attach/detach remote audio tracks
  const handleTrackSubscribed = (
    track: RemoteTrack,
    _publication: RemoteTrackPublication,
    _participant: RemoteParticipant,
  ) => {
    if (track.kind === Track.Kind.Audio) {
      const el = track.attach();
      el.autoplay = true;
      el.hidden = true;
      audioContainerRef.current?.appendChild(el);
    }
  };

  const handleTrackUnsubscribed = (track: RemoteTrack) => {
    track.detach().forEach((el) => el.remove());
  };

  // Check and set agent status
  const checkAndSetAgentStatus = useCallback(() => {
    if (!room) return;

    const agent = Array.from(room.remoteParticipants.values()).find(
      (p) => p.kind === "agent" || p.identity.startsWith("agent-"),
    );
    setIsAgentConnected(!!agent);
  }, [room]);

  // Connect to LiveKit room
  const connectToRoom = async (
    overrideUrl?: string,
    overrideToken?: string,
  ) => {
    try {
      setIsConnecting(true);
      const effectiveUrl = overrideUrl || livekitUrl;
      const effectiveToken = overrideToken || accessToken;

      if (!effectiveUrl || !effectiveToken) {
        console.warn("LiveKit credentials are missing");
        return;
      }

      const lkRoom = new Room();
      await lkRoom.connect(effectiveUrl, effectiveToken);

      // Wire room events
      lkRoom.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      lkRoom.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      lkRoom.on(RoomEvent.Disconnected, () => {
        setRoom(null);
        setIsAgentConnected(false);
      });

      // Listen for participant connections
      lkRoom.on(
        RoomEvent.ParticipantConnected,
        (participant: RemoteParticipant) => {
          if (
            participant.kind === "agent" ||
            participant.identity.startsWith("agent-")
          ) {
            setIsAgentConnected(true);
          }
        },
      );

      lkRoom.on(
        RoomEvent.ParticipantDisconnected,
        (participant: RemoteParticipant) => {
          if (
            participant.kind === "agent" ||
            participant.identity.startsWith("agent-")
          ) {
            setIsAgentConnected(false);
          }
        },
      );

      // Listen for transcription events
      lkRoom.registerTextStreamHandler(
        "lk.transcription",
        async (reader, pInfo) => {
          try {
            const text = await reader.readAll();

            setTranscripts((prev) => [
              ...prev,
              {
                identity: pInfo.identity,
                text: text,
                timestamp: new Date(),
                isAgent:
                  pInfo.kind === "agent" || pInfo.identity.startsWith("agent-"),
              },
            ]);
          } catch (error) {
            console.error("Error reading transcription:", error);
          }
        },
      );

      setRoom(lkRoom);
      setIsMuted(false);

      // Enable microphone by default
      try {
        await lkRoom.localParticipant.setMicrophoneEnabled(true);
      } catch (e) {
        console.warn("Microphone enable failed:", e);
      }
    } catch (err) {
      console.error("LiveKit connect error", err);
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect from room
  const disconnectRoom = () => {
    try {
      room?.disconnect();
    } catch (_) {
      // noop
    } finally {
      setRoom(null);
      setIsAgentConnected(false);
    }
  };

  // Handle mute/unmute
  const toggleMute = async () => {
    if (!room) return;

    const nextMuted = !isMuted;
    setIsMuted(nextMuted);

    try {
      await room.localParticipant.setMicrophoneEnabled(!nextMuted);
    } catch (error) {
      console.error("Failed to toggle microphone:", error);
      setIsMuted(!nextMuted); // Revert state on error
    }
  };

  // Handle end call
  const handleEndCall = () => {
    setShowEndCallPopup(true);
  };

  const confirmEndCall = () => {
    disconnectRoom();
    setShowEndCallPopup(false);
    navigate(`/content/spoken-dialogue/${id}`);
  };

  const cancelEndCall = () => {
    setShowEndCallPopup(false);
  };

  // Initialize connection when component mounts
  useEffect(() => {
    if (!id) return;

    const initializeConnection = async () => {
      try {
        // Get LiveKit URL from environment or use default
        const envUrl = (import.meta as any)?.env?.VITE_LIVEKIT_URL as
          | string
          | undefined;
        const url =
          envUrl || "wss://madlen-spoken-dialogue-dev-9ne42nkq.livekit.cloud";
        setLivekitUrl(url);

        // For now, we'll use a mock token. In production, this should come from your backend
        // You'll need to implement a token endpoint that generates LiveKit tokens
        const mockToken = "mock_token_placeholder"; // Replace with actual token generation
        setAccessToken(mockToken);

        // Connect to room
        await connectToRoom(url, mockToken);
      } catch (error) {
        console.error("Failed to initialize connection:", error);
      }
    };

    initializeConnection();
  }, [id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (room) {
        try {
          room.disconnect();
        } catch (_) {
          // noop
        }
      }
    };
  }, [room]);

  // Get connection status text
  const getConnectionStatusText = () => {
    if (isConnecting) return t("spokenDialogue.detail.connecting");
    if (!room) return t("spokenDialogue.detail.disconnected");
    if (room.state === "connected") return t("spokenDialogue.detail.connected");
    if (room.state === "connecting")
      return t("spokenDialogue.detail.connecting");
    if (room.state === "reconnecting")
      return t("spokenDialogue.detail.reconnecting");
    return t("spokenDialogue.detail.disconnected");
  };

  // Get connection status icon
  const getConnectionStatusIcon = () => {
    if (isConnecting || (room && room.state === "connecting")) {
      return (
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      );
    }
    if (room && room.state === "connected") {
      return <Wifi className="w-4 h-4 text-green-500" />;
    }
    return <WifiOff className="w-4 h-4 text-red-500" />;
  };

  return (
    <div className="flex-1 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            onClick={() => navigate(`/content/spoken-dialogue/${id}`)}
            size="md"
            rounded="full"
            variant="outline"
            leftIcon={<ArrowLeft size={18} />}
          >
            {t("common.back")}
          </Button>
          <Text variant="h1" className="text-2xl font-semibold">
            {t("spokenDialogue.detail.practiceTitle")}
          </Text>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Control Panel */}
          <div className="bg-white dark:bg-dark-600 rounded-2xl shadow-lg p-6 flex flex-col items-center justify-center border border-gray-200 dark:border-gray-700 h-full min-h-[300px] lg:min-h-0">
            <Text variant="h2" className="mb-4 text-center">
              {isAgentConnected
                ? t("spokenDialogue.detail.conversationInProgress")
                : t("spokenDialogue.detail.waitingForAgent")}
            </Text>

            {/* Connection Status */}
            <div className="mb-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                {getConnectionStatusIcon()}
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {getConnectionStatusText()}
                </span>
              </div>
            </div>

            {/* Microphone Control */}
            <div className="mb-6">
              <Button
                onClick={toggleMute}
                size="lg"
                rounded="full"
                variant={isMuted ? "outline" : "primary"}
                leftIcon={isMuted ? <MicOff size={20} /> : <Mic size={20} />}
                className="min-w-[120px]"
              >
                {isMuted
                  ? t("spokenDialogue.detail.unmute")
                  : t("spokenDialogue.detail.mute")}
              </Button>
            </div>

            {/* End Call Button */}
            <Button
              onClick={handleEndCall}
              size="lg"
              rounded="full"
              variant="danger"
              leftIcon={<PhoneOff size={20} />}
              className="min-w-[120px]"
            >
              {t("spokenDialogue.detail.endCallConfirm")}
            </Button>

            {/* Footer */}
            <div className="mt-auto pt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
              <Text variant="p" className="text-xs">
                {t("spokenDialogue.detail.controlPanel")}
              </Text>
            </div>
          </div>

          {/* Right Column: Transcript Panel */}
          <div className="bg-white dark:bg-dark-600 rounded-2xl shadow-lg p-6 flex flex-col border border-gray-200 dark:border-gray-700 h-[70vh] lg:h-full">
            <Text
              variant="h2"
              className="mb-4 border-b dark:border-gray-700 pb-2"
            >
              {t("spokenDialogue.detail.transcript")}
            </Text>

            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
              {transcripts.length === 0 && (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  {t("spokenDialogue.detail.waitingForConversation")}
                </div>
              )}

              {transcripts.map((entry, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 my-4 ${!entry.isAgent ? "justify-end" : ""}`}
                >
                  {entry.isAgent && (
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
                      <Bot className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                  )}

                  <div
                    className={`p-3 rounded-lg max-w-md ${
                      entry.isAgent
                        ? "bg-gray-100 dark:bg-dark-500 text-gray-800 dark:text-gray-200"
                        : "bg-indigo-500 text-white"
                    }`}
                  >
                    <p className="text-sm">{entry.text}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {entry.timestamp.toLocaleTimeString()}
                    </p>
                  </div>

                  {!entry.isAgent && (
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-200 dark:bg-dark-400 rounded-full flex items-center justify-center">
                      <Mic className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                    </div>
                  )}
                </div>
              ))}

              <div ref={transcriptEndRef} />
            </div>
          </div>
        </div>
      </div>

      {/* End Call Confirmation Popup */}
      {showEndCallPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={cancelEndCall}
          />
          <div className="relative w-full max-w-md">
            <div className="rounded-3xl bg-white dark:bg-dark-600 shadow-2xl overflow-hidden p-8 text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-rose-100 dark:bg-rose-900/30 border border-rose-200/30 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-rose-400" />
                </div>
              </div>

              <Text variant="h3" className="text-xl font-semibold mb-2">
                {t("spokenDialogue.detail.endCall")}
              </Text>

              <Text
                variant="p"
                className="text-gray-600 dark:text-gray-400 text-sm mb-6"
              >
                {t("spokenDialogue.detail.endCallDescription")}
              </Text>

              <div className="flex gap-3">
                <Button
                  onClick={cancelEndCall}
                  variant="outline"
                  className="flex-1"
                >
                  {t("spokenDialogue.detail.cancel")}
                </Button>

                <Button
                  onClick={confirmEndCall}
                  variant="danger"
                  className="flex-1"
                >
                  {t("spokenDialogue.detail.endCallConfirm")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden container to attach remote audio elements */}
      <div ref={audioContainerRef} style={{ display: "none" }} />
    </div>
  );
};

export default ConversationPracticeView;
